import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  TextField,
  Pagination,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React from "react";
import { reduceObj } from "../../common/services/Utilities";
import { KeyValue } from "../../interfaces";
import MenuPopup from "../MenuPopup";

const PREFIX = "MenuCheckForm";

const classes = {
  inputStyle: `${PREFIX}-inputStyle`,
  paginationComponent: `${PREFIX}-paginationComponent`,
};

const Root = styled("div")(() => ({
  [`& .${classes.inputStyle}`]: {
    "&::WebkitOuterSpinButton, &::WebkitInnerSpinButton": {
      WebkitAppearance: "none",
    },
    "textAlign": "center",
    "padding": "7px 14px",
    "width": "30",
  },

  [`& .${classes.paginationComponent}`]: {
    paddingTop: 12,
    paddingBottom: 22,
    minHeight: 24,
  },
}));

interface Props {
  type: string;
  options: string[];
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  checkedStatus: KeyValue;
  setCheckedStatus: (checkedStatus: KeyValue) => void;
  disabled?: boolean;
  error?: boolean;
  pageSize?: number;
}

const handleSelectAll = (
  event: React.ChangeEvent<HTMLInputElement>,
  options: string[],
  setSelection: (checkedStatus: KeyValue) => void,
) => {
  setSelection(
    reduceObj(
      options.map((val: string) => {
        return { key: val, value: event.target.checked };
      }),
    ),
  );
};

export default function MenuCheckForm({
  type,
  options,
  onChange,
  checkedStatus,
  setCheckedStatus,
  disabled,
  error,
  pageSize,
}: Props) {
  const endPage = pageSize ? pageSize : 25;
  const [page, setPage] = React.useState(1);
  const [pagination, setPagination] = React.useState({
    start: 0,
    end: endPage,
  });
  const handleChange = (event: React.ChangeEvent<unknown>, page: number) => {
    const start = (page - 1) * endPage;
    const end = start + endPage;
    setPage(page);
    setPagination({ start, end });
  };

  const paginatedOptions = Object.values(
    Object.fromEntries(
      Object.entries(options).slice(pagination.start, pagination.end),
    ),
  );

  const paginationComponent = (category: string[]) => {
    return (
      <Box
        className={classes.paginationComponent}
        sx={{
          justifyContent: "center",
          alignItems: "center",
          display: "flex",
        }}
      >
        <Pagination
          shape="rounded"
          id="counties_pagination"
          color="primary"
          count={Math.ceil(category.length / endPage)}
          onChange={handleChange}
          page={page}
        />
        <TextField
          id="outlined-basic"
          type={"number"}
          variant="outlined"
          onChange={(e) => {
            e.target.value !== "" &&
              Number(e.target.value) <= Math.ceil(category.length / endPage) &&
              handleChange(e, Number(e.target.value));
          }}
          slotProps={{
            input: {
              classes: { input: classes.inputStyle },
              inputProps: {
                min: 1,
                max: Math.ceil(category.length / endPage),
              },
            },
          }}
        />
      </Box>
    );
  };
  return (
    <Root>
      <MenuPopup
        pagination={paginationComponent(options)}
        disabled={disabled}
        id={`selected-${type.replace(/ /g, "-").toLowerCase()}`}
        width={"small"}
        popperPosition={"right"}
        label={
          <>
            <span
              style={
                error
                  ? { display: "block", fontWeight: "bold", color: "#CC0000" }
                  : { display: "block", fontWeight: "bold" }
              }
              id={type}
            >{`Selected ${type}`}</span>
            <span
              style={
                error
                  ? { display: "block", color: "#CC0000", fontSize: 14 }
                  : { display: "block" }
              }
              id={`all_${type}`}
            >
              {error
                ? "At least one selection must be made"
                : Object.values(checkedStatus).indexOf(false) > -1
                  ? `Custom ${type} Selections`
                  : `All ${type}`}
            </span>
          </>
        }
      >
        <Grid container>
          <FormControl component="fieldset">
            <FormLabel
              component="legend"
              style={{ fontSize: "1.25rem", color: "#1A4480" }}
            >
              Filter results with these selections.
            </FormLabel>
            <FormControlLabel
              control={
                <Checkbox
                  id="select_all"
                  color="primary"
                  disableFocusRipple
                  checked={
                    !!Object.keys(checkedStatus).every(
                      (checked) => checkedStatus[checked],
                    )
                  }
                  inputProps={{ "aria-label": "checkbox" }}
                  onChange={(e: any) =>
                    handleSelectAll(e, options, setCheckedStatus)
                  }
                  name={"Select All / None"}
                />
              }
              label={"Select All / None"}
            />
          </FormControl>

          <FormGroup>
            {paginatedOptions.map((e: string, index) => {
              return (
                <FormControlLabel
                  key={index}
                  style={{ whiteSpace: "nowrap" }}
                  control={
                    <Checkbox
                      id={`id_${e}`}
                      key={index}
                      disabled={false}
                      color="primary"
                      disableFocusRipple
                      checked={!!checkedStatus[e]}
                      onChange={onChange}
                      name={e}
                    />
                  }
                  label={e}
                />
              );
            })}
          </FormGroup>
        </Grid>
      </MenuPopup>
    </Root>
  );
}
